import { Box } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetMainComponentTypeList } from 'src/api/main-component-type'
import {
  invalidateMaintenanceTemplateCache,
  useGetMaintenanceTemplate,
  useUpdateMaintenanceTemplate,
} from 'src/api/maintenanceTemplate'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { AppForm, FormSelectField, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { MaintenanceTemplateType } from 'src/enums'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { ILocalityEquipmentMaintenance } from 'src/generated-types/maintenance'
import { IMaintenanceTemplate } from 'src/generated-types/maintenanceTemplate'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { getTranslatedText, isNumeric } from 'src/utils'
import { z } from 'zod'
import MaintenanceTemplatesMaintenanceContainer from './MaintenanceTemplatesMaintenanceContainer'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { useDialogCtx } from 'src/hooks/context-hooks'
interface EditMaintenanceTemplateProps extends ShowDialogProps {
  customerId: EntityId
  type: MaintenanceTemplateType
  id: EntityId
}

const EditMaintenanceTemplateForm: React.FC<EditMaintenanceTemplateProps> = ({ pop, customerId, type, id }) => {
  const { t: translate } = useTranslation(['maintenance', 'common', 'common-api-message'])
  const { data: maintenanceTemplate } = useGetMaintenanceTemplate(customerId, id)
  const maintenanceTemplateDetails = maintenanceTemplate as IMaintenanceTemplate
  const { mutateAsync, isLoading } = useUpdateMaintenanceTemplate(customerId, id)
  const { data: mainComponentTypes } = useGetMainComponentTypeList(customerId as EntityId)
  const [maintenances, setMaintenances] = useState<ILocalityEquipmentMaintenance[]>([])
  const originalMaintenancesRef = useRef<ILocalityEquipmentMaintenance[]>([])
  const originalTemplateDetailsRef = useRef<any>(null)
  const [maintenanceChanged, setmaintenanceChanged] = useState(false)
  const [modifiedMaintenanceIds, setModifiedMaintenanceIds] = useState<number[]>([])
  const { showConfirmDialog, showWarningDialog, closeAllDialogs } = useDialogCtx()

  const genericSchema = z.object({ id: z.number() })
  const genericMaintenanceSchema = z.object({ localityId: z.number() })
  const maintenanceTemplateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(400),
    type: z.number().default(type),
    mainComponentTypeMaintenanceTemplates: z.array(genericSchema).nullable().optional(),
    maintenances: z.array(genericMaintenanceSchema).nullable().optional(),
  })

  const form = useZodForm(maintenanceTemplateSchema)
  const anyChanges = form.formState.isDirty || maintenanceChanged

  useEffect(() => {
    if (maintenanceTemplateDetails) setMaintenances(maintenanceTemplateDetails.maintenances)
  }, [maintenanceTemplateDetails])

  useEffect(() => {
    if (maintenanceTemplateDetails) {
      const initialMaintenances = maintenanceTemplateDetails.maintenances
      setMaintenances(initialMaintenances)
      originalMaintenancesRef.current = JSON.parse(JSON.stringify(initialMaintenances))
    }
  }, [maintenanceTemplateDetails])

  useEffect(() => {
    if (originalMaintenancesRef.current.length > 0) {
      const currentJSON = JSON.stringify(maintenances)
      const originalJSON = JSON.stringify(originalMaintenancesRef.current)
      setmaintenanceChanged(currentJSON !== originalJSON)

      const modifiedIds: number[] = []

      maintenances.forEach(maintenance => {
        const original = originalMaintenancesRef.current.find(m => m.id === maintenance.id)
        if (!original || JSON.stringify(maintenance) !== JSON.stringify(original)) {
          modifiedIds.push(maintenance.id)
        }
      })

      setModifiedMaintenanceIds(modifiedIds)
    }
  }, [maintenances])

  let mainComponentTypeArray: (IMainComponentType | undefined)[] = []

  useEffect(() => {
    if (mainComponentTypes != null && type === MaintenanceTemplateType.Equipment) {
      mainComponentTypeArray =
        maintenanceTemplateDetails?.mainComponentTypeMaintenanceTemplates?.map(m => {
          return mainComponentTypes?.find(s => s.id == m.mainComponentTypeId)
        }) || []
    }

    if (maintenanceTemplateDetails != null && mainComponentTypes != null) {
      const initialFormValues = {
        name: maintenanceTemplateDetails.name,
        mainComponentTypeMaintenanceTemplates:
          type === MaintenanceTemplateType.Equipment ? mainComponentTypeArray : null,
        description: maintenanceTemplateDetails.description,
      }

      form.reset(initialFormValues)
      originalTemplateDetailsRef.current = { ...initialFormValues }
    }
  }, [maintenanceTemplate, mainComponentTypes, form, id])

  const onSubmit = useCallback(
    async form => {
      if (maintenances.length == 0) {
        toast.warning(translate('maintenance-template-requires-maintenances', { ns: 'maintenance' }))
        return
      }

      if (type == MaintenanceTemplateType.Equipment) {
        form.mainComponentTypeMaintenanceTemplates = form.mainComponentTypeMaintenanceTemplates.map(m => ({
          mainComponentTypeId: m.id,
        }))
      }
      const updatedMaintenances: ILocalityEquipmentMaintenance[] = []
      maintenances.map(i => {
        if (!isNumeric(i.id)) i.id = 0
        updatedMaintenances.push(i)
        return i
      })
      form.maintenances = updatedMaintenances

      await mutateAsync(form, {
        onSuccess: () => {
          if (type == MaintenanceTemplateType.Equipment) {
            invalidateMaintenanceTemplateCache.getEquipmentMaintenanceTemplates(customerId as EntityId)
            invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId as EntityId)
            invalidateMaintenanceCache.invalidateAllMaintenances(customerId)
          }
          if (type == MaintenanceTemplateType.Locality) {
            invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(customerId as EntityId)
            invalidateScheduledMaintenanceCache.getLocalityScheduledMaintenances(customerId as EntityId)
          }
          toast.success(translate('maintenance-template-updated-success', { ns: 'maintenance' }))
          pop()
          showWarningDialog(
            '',
            translate('scheduled-maintenances-background-message', { ns: 'maintenance' }),
            false,
            [
              {
                text: translate('ok', { ns: 'form-schema' }),
                variant: 'contained',
                callback: () => closeAllDialogs(),
              },
            ],
            'info',
          )
        },
        onError: () => {
          toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
        },
      })
    },
    [maintenances, type, mutateAsync, customerId, translate, pop, showConfirmDialog, showWarningDialog, closeAllDialogs],
  )

  const handleClose = useCallback(() => {
    if (anyChanges) {
      showConfirmDialog(
        translate('confirm-discard-changes', { ns: 'common' }),
        translate('unsaved-maintenance-changes-warning', { ns: 'maintenance' }),
        {
          acceptText: translate('discard-changes', { ns: 'common' }),
          cancelText: translate('cancel', { ns: 'common' }),
        },
        () => pop(),
      )
    } else {
      pop()
    }
  }, [anyChanges, translate])

  return (
    <div>
      <GenericDialogLayout
        title={
          type === MaintenanceTemplateType.Equipment
            ? translate('update-equipment-maintenance-template', { ns: 'maintenance' })
            : translate('update-locality-maintenance-template', { ns: 'maintenance' })
        }
        pop={handleClose}
        removedCloseButton={true}
        sx={{ overflow: 'hidden' }}
        actions={[
          anyChanges && (
            <MainButton variant="outlined" color="primary" key="discard-button" onClick={handleClose}>
              {translate('discard-changes', { ns: 'common' })}
            </MainButton>
          ),
          <MainButton
            variant="contained"
            key="add-button"
            onClick={() => {
              form.handleSubmit(onSubmit)()
            }}
            disabled={!anyChanges}
            loading={isLoading}
          >
            {translate('update-&-exit', { ns: 'common' })}
          </MainButton>,
        ].filter(Boolean)}
      >
        <Box>
          {maintenanceTemplateDetails && (
            <AppForm
              form={form}
              onSubmit={form => {
                onSubmit(form)
              }}
            >
              <Flex.Row>
                <Flex.Column width={type === MaintenanceTemplateType.Locality ? '100%' : '60%'}>
                  <FormTextField name="name" label={translate('title', { ns: 'common' })} />
                </Flex.Column>
                {type === MaintenanceTemplateType.Equipment && (
                  <Flex.Column width={'40%'}>
                    <FormSelectField
                      name="mainComponentTypeMaintenanceTemplates"
                      data={(mainComponentTypes as IMainComponentType[]) || []}
                      label={translate('type-of-equipment', { ns: 'maintenance' })}
                      getOptionLabel={option => getTranslatedText(option.name)}
                      renderOption={(props: any, option) => (
                        <Box {...props} key={option.id}>
                          {getTranslatedText(option.name)}
                        </Box>
                      )}
                      multiple
                      defaultValue={[]}
                      clearOnBlur={false}
                      clearOnEscape={false}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                    />
                  </Flex.Column>
                )}
              </Flex.Row>
              <FormTextField name="description" label={translate('description', { ns: 'common' })} />
            </AppForm>
          )}
        </Box>
        <Box>
          {maintenanceTemplateDetails && (
            <MaintenanceTemplatesMaintenanceContainer
              maintenances={maintenances}
              setMaintenances={setMaintenances}
              modifiedMaintenanceIds={modifiedMaintenanceIds}
              isUpdating={isLoading}
            />
          )}
        </Box>
      </GenericDialogLayout>
    </div>
  )
}

export default EditMaintenanceTemplateForm
